/**
 * Footer Component - Responsive Design Patterns 2025
 * Baseado em best practices 2025: Utility-only, semantic HTML, CSS Grid
 *
 * Padrão:
 * - Mobile: Stacked layout (1 coluna)
 * - Tablet: 2 colunas
 * - Desktop: 3-4 colunas com CSS Grid
 *
 * Referências:
 * - https://www.eleken.co/blog-posts/footer-ux
 * - https://dev.to/tailwindcss/21-top-react-footer-components-to-use-in-2024-4e8o
 */

interface FooterProps {
  variant?: 'utility' | 'full';
}

export function Footer({ variant = 'utility' }: FooterProps) {
  if (variant === 'utility') {
    return <UtilityFooter />;
  }

  return <FullFooter />;
}

/**
 * Utility-only Footer
 * Minimal com apenas links essenciais e informações legais
 */
function UtilityFooter() {
  return (
    <footer className="bg-white border-t border-gray-200 mt-auto">
      <div className="w-full px-4 py-6 sm:px-6 lg:max-w-7xl lg:mx-auto">
        {/* Mobile: Stack vertical, Desktop: Flex horizontal */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">

          {/* Copyright */}
          <div className="text-sm text-gray-600 text-center sm:text-left">
            © {new Date().getFullYear()} Receita Federal - Sistema de Investigações
          </div>

          {/* Links úteis */}
          <nav className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6">
            <a
              href="#sobre"
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center"
            >
              Sobre
            </a>
            <a
              href="#documentacao"
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center"
            >
              Documentação
            </a>
            <a
              href="#suporte"
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center"
            >
              Suporte
            </a>
            <a
              href="#privacidade"
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center"
            >
              Privacidade
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

/**
 * Full Footer
 * Com múltiplas seções usando CSS Grid responsivo
 */
function FullFooter() {
  return (
    <footer className="bg-gray-50 border-t border-gray-200 mt-auto">
      <div className="w-full px-4 py-8 sm:px-6 lg:max-w-7xl lg:mx-auto">

        {/* Grid responsivo: 1 col (mobile), 2 cols (tablet), 4 cols (desktop) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Coluna 1: Sobre */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Sobre o Sistema
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#visao-geral" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Visão Geral
                </a>
              </li>
              <li>
                <a href="#como-funciona" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Como Funciona
                </a>
              </li>
              <li>
                <a href="#casos-uso" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Casos de Uso
                </a>
              </li>
            </ul>
          </div>

          {/* Coluna 2: Recursos */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Recursos
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#documentacao" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Documentação
                </a>
              </li>
              <li>
                <a href="#api-docs" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  API Reference
                </a>
              </li>
              <li>
                <a href="#tutoriais" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Tutoriais
                </a>
              </li>
              <li>
                <a href="#changelog" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Changelog
                </a>
              </li>
            </ul>
          </div>

          {/* Coluna 3: Suporte */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Suporte
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#faq" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  FAQ
                </a>
              </li>
              <li>
                <a href="#contato" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Contato
                </a>
              </li>
              <li>
                <a href="#status" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Status do Sistema
                </a>
              </li>
            </ul>
          </div>

          {/* Coluna 4: Legal */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Legal
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#termos" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Termos de Uso
                </a>
              </li>
              <li>
                <a href="#privacidade" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  Política de Privacidade
                </a>
              </li>
              <li>
                <a href="#lgpd" className="text-sm text-gray-600 hover:text-blue-600 transition-colors min-h-[44px] flex items-center">
                  LGPD
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8" />

        {/* Bottom bar - Mobile: Stack, Desktop: Flex */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="text-sm text-gray-600 text-center sm:text-left">
            © {new Date().getFullYear()} Receita Federal do Brasil. Todos os direitos reservados.
          </div>

          <div className="text-xs text-gray-500 text-center sm:text-right">
            Desenvolvido com React + Vite + Cytoscape.js
          </div>
        </div>
      </div>
    </footer>
  );
}
